/**
 * OrderableModeToggle — admin/supervisor-only switch between the regular
 * "commandable products only" picker and a full catalogue view.
 *
 * Why: occasionally an admin needs to order a product that's been temporarily
 * deactivated (`commandable=false`) — typically out-of-stock substitutes or
 * one-off purchases. Rather than re-flipping the `commandable` flag each time,
 * an admin/supervisor can switch to "Tous" to see the whole catalogue (both
 * commandable and non-commandable) and pick whichever fits.
 *
 * Mode === 'normal' (default): only commandable products are listed.
 * Mode === 'all': all products are listed (commandable + non-commandable).
 *
 * The component renders nothing for non-privileged users — children/parents
 * should treat it as a no-op for them.
 */
import React from 'react';
import { Package, PackageOpen } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const OrderableModeToggle = ({ mode, onChange, className = '' }) => {
    const { user } = useAuth();
    // Permission: admin OR supervisor only. Regular livreur/readonly never
    // see this toggle (the parent's product filter then defaults to normal).
    if (!user || !['admin', 'supervisor'].includes(user.role)) return null;

    return (
        <div
            className={`inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 p-0.5 ${className}`}
            data-testid="orderable-mode-toggle"
        >
            <button
                type="button"
                onClick={() => onChange('normal')}
                className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                    mode === 'normal'
                        ? 'bg-white text-slate-900 shadow-sm font-medium'
                        : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="orderable-mode-normal"
                title="Mode normal — afficher les produits commandables uniquement"
            >
                <Package className="w-3.5 h-3.5" />
                Commandables
            </button>
            <button
                type="button"
                onClick={() => onChange('all')}
                className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                    mode === 'all'
                        ? 'bg-amber-100 text-amber-800 shadow-sm font-medium'
                        : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="orderable-mode-all"
                title="Mode complet — afficher tous les produits, y compris les non-commandables"
            >
                <PackageOpen className="w-3.5 h-3.5" />
                Tous
            </button>
        </div>
    );
};

export default OrderableModeToggle;
